import { DeeplinkKeys } from '@/consts/deepLinks';
import extractUrlParams from '@/utils/ui/extractUrlParams';

import { DeeplinkKind, type Deeplink } from './types';

/**
 * Reads the social-share payload out of `?state=`, if that is what it holds.
 *
 * `state` is not ours alone. OAuth uses the same parameter name, so every
 * sign-in return trip arrives carrying one — `?state=029aca3a-209f-…` — and
 * `JSON.parse` on that throws. This runs inside a `useState` initializer during
 * `useAppBootstrap`, so the throw escaped render and React tore down the whole
 * tree: the app went blank after signing in, and came back only on a reload
 * because by then the parameter was gone.
 *
 * Swallowed deliberately, and not logged: a `state` that is not our JSON is the
 * normal case on any host that also does OAuth, not an error. It simply means
 * this is not a social-share deeplink.
 */
const parseSocialShareFileUrl = (
  rawState: string | undefined
): string | null => {
  if (!rawState) return null;

  let parsed: { fileUrl?: unknown; miniapp?: unknown } | null = null;
  try {
    parsed = JSON.parse(rawState);
  } catch {
    return null;
  }

  if (
    parsed &&
    typeof parsed.fileUrl === 'string' &&
    parsed.fileUrl &&
    parsed.miniapp === true
  ) {
    return parsed.fileUrl;
  }

  return null;
};

/**
 * Priority-ordered table mapping URL params to typed deeplinks.
 * Adding a new deeplink kind means one entry here + one branch in the
 * Deeplink union — no changes to consumers.
 *
 * Priority is encoded by array order:
 *   task > driveFile > projectJson > preset > category > tool
 */
const PRIORITY: Array<{
  key: DeeplinkKeys;
  build: (
    value: string,
    params: Partial<Record<DeeplinkKeys, string>>
  ) => Deeplink;
}> = [
  {
    key: DeeplinkKeys.taskId,
    build: (taskId) => ({ kind: DeeplinkKind.Task, taskId }),
  },
  {
    key: DeeplinkKeys.fileUid,
    build: (fileUid) => ({ kind: DeeplinkKind.DriveFile, fileUid }),
  },
  {
    key: DeeplinkKeys.presetJsonUrl,
    build: (jsonUrl) => ({ kind: DeeplinkKind.ProjectJson, jsonUrl }),
  },
  {
    key: DeeplinkKeys.presetId,
    build: (presetId, params) => ({
      kind: DeeplinkKind.Preset,
      presetId,
      category: params[DeeplinkKeys.category],
      openChooser: params[DeeplinkKeys.openChooser] === 'true',
    }),
  },
  {
    key: DeeplinkKeys.category,
    build: (category, params) => ({
      kind: DeeplinkKind.Category,
      category,
      seeAll: params[DeeplinkKeys.seeAll] === 'true',
    }),
  },
  {
    key: DeeplinkKeys.tool,
    build: (tool) => ({ kind: DeeplinkKind.Tool, tool }),
  },
];

const parseDeeplinks = (): Deeplink => {
  const rawState = extractUrlParams([DeeplinkKeys.socialShareState])[
    DeeplinkKeys.socialShareState
  ];
  const socialShareFileUrl = parseSocialShareFileUrl(rawState);
  if (socialShareFileUrl) {
    return { kind: DeeplinkKind.SocialShare, fileUrl: socialShareFileUrl };
  }

  // Trigger params (the PRIORITY keys) plus the modifiers a `build` reads off
  // the same params object — a modifier never selects a deeplink on its own, so
  // it must not join the priority table.
  const params = extractUrlParams([
    ...PRIORITY.map((entry) => entry.key),
    DeeplinkKeys.openChooser,
    DeeplinkKeys.seeAll,
  ]);
  const match = PRIORITY.find(({ key }) => params[key]);
  if (!match) return { kind: DeeplinkKind.None };
  return match.build(params[match.key] as string, params);
};

export default parseDeeplinks;
